import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { FaUnlink } from 'react-icons/fa';
import api from 'services/api';
import { cooperplaceApi } from 'services/api';
import Button from 'v3/components/Button';
import DataTable from 'v3/components/DataTable';
import Modal from 'v3/components/Modal';
import Text from 'v3/components/Text';
import useModal from 'hooks/useModal';
import { useParams } from 'react-router-dom';
import { UserContext } from 'contexts/UserContext';
import { useUser } from '../context';
import AddPermissionModal from './PermissionsModalAdd';

function formatDate(value) {
  if (!value) return 'Não informado';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Não informado';
  return date.toLocaleDateString('pt-BR');
}

function getCurrentUsername() {
  try {
    const user = JSON.parse(localStorage.getItem('usuario') || 'null');
    return user?.username || 'Sistema';
  } catch (error) {
    return 'Sistema';
  }
}

export default function PermissionsModal({ isOpen, onClose }) {
  const user = useUser();
  const { refreshPermissions } = useContext(UserContext);
  const params = useParams();
  const addModal = useModal();
  const [permissionOptions, setPermissionOptions] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [initialPermissions, setInitialPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const permissions = user.data?.customPermissions || [];
  const isEditing = !!params.id;

  const permissionOptionsMap = useMemo(() => {
    return new Map(permissionOptions.map(option => [option.id, option]));
  }, [permissionOptions]);

  const normalizePermissions = items => {
    return (items || []).map(item => {
      const permissionId = item.permissionId ?? item.id;
      const option = permissionOptionsMap.get(permissionId);
      const action =
        typeof item.action === 'boolean'
          ? item.action
            ? 'allow'
            : 'deny'
          : item.action || 'allow';

      return {
        id: permissionId,
        name: item.name || option?.name,
        group: item.group || option?.group,
        slug: item.slug || option?.slug,
        action,
        includedBy: item.createdBy || item.includedBy || 'Sistema',
        includedAt: item.createdAt || item.includedAt || new Date().toISOString(),
      };
    });
  };

  async function fetchUserPermissions({ page, perPageSize, searchTerm } = {}) {
    try {
      setIsLoading(true);
      const resolvedPage = page ?? currentPage;
      const resolvedPerPage = perPageSize ?? perPage;
      const response = await cooperplaceApi.get(`users/${params.id}/permissions`, {
        params: {
          page: resolvedPage,
          perPage: resolvedPerPage,
          search: searchTerm ?? search,
        },
      });
      const payload = response.data || {};
      const normalized = normalizePermissions(payload.data || []);
      const meta = payload.meta || {};
      user.setData({ customPermissions: normalized });
      setInitialPermissions(normalized.map(permission => ({ ...permission })));
      setTotalItems(meta.total || normalized.length);
      setTotalPages(meta.last_page || meta.lastPage || 1);
      if (meta.current_page && meta.current_page !== currentPage) {
        setCurrentPage(meta.current_page);
      }
      if (meta.per_page && meta.per_page !== perPage) {
        setPerPage(meta.per_page);
      }
    } catch (error) {
      let message = 'Erro ao buscar permissões adicionais'
      if (error?.response?.data?.error) {
        message = error.response.data.error
      }
      user.showSnackbar(message, { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (isOpen && isEditing) {
      fetchUserPermissions();
    }
  }, [isOpen, isEditing, currentPage, perPage]);

  useEffect(() => {
    async function fetchPermissions() {
      try {
        const response = await api.get('permissions');
        setPermissionOptions(response.data || []);
      } catch (fetchError) {
        setPermissionOptions([]);
      }
    }

    fetchPermissions();
  }, []);

  useEffect(() => {
    if (!isEditing || permissions.length === 0) return;

    const hasMissingDetails = permissions.some(
      permission => !permission.name || !permission.group || !permission.slug
    );

    if (!hasMissingDetails) return;

    const normalized = normalizePermissions(permissions);
    user.setData({ customPermissions: normalized });
    setInitialPermissions(normalized.map(permission => ({ ...permission })));
  }, [permissionOptions]);

  const filteredPermissions = useMemo(() => {
    if (isEditing) return permissions;
    if (!search) return permissions;
    const searchValue = search.toLowerCase();
    return permissions.filter(permission => {
      const name = permission.name?.toLowerCase() || '';
      const group = permission.group?.toLowerCase() || '';
      return name.includes(searchValue) || group.includes(searchValue);
    });
  }, [permissions, search, isEditing]);

  const pages = useMemo(() => {
    if (isEditing) return totalPages;
    return Math.max(1, Math.ceil(filteredPermissions.length / perPage));
  }, [filteredPermissions.length, perPage, isEditing, totalPages]);

  const pagedPermissions = useMemo(() => {
    if (isEditing) return filteredPermissions;
    const start = (currentPage - 1) * perPage;
    return filteredPermissions.slice(start, start + perPage);
  }, [filteredPermissions, currentPage, perPage, isEditing]);

  const availablePermissionOptions = useMemo(() => {
    const selectedIds = new Set(permissions.map(item => item.id));
    return permissionOptions.filter(option => !selectedIds.has(option.id));
  }, [permissionOptions, permissions]);

  function handleModalClose() {
    setSearch('');
    setCurrentPage(1);
    addModal.close();
    if (isEditing) {
      user.setData({ customPermissions: initialPermissions });
      if (user.fetchUserAudit) {
        user.fetchUserAudit();
      }
      // As alterações já foram gravadas; se o usuário editado for o logado, o portal passa a vê-las.
      refreshPermissions().catch(() => {});
    }
    onClose();
  }

  async function handleAddPermission(permission, action) {
    if (!isEditing) {
      const newPermission = {
        id: permission.id,
        name: permission.name,
        group: permission.group,
        slug: permission.slug,
        action,
        includedBy: getCurrentUsername(),
        includedAt: new Date().toISOString(),
      };

      user.setData({
        customPermissions: [...permissions, newPermission],
      });
      return true;
    }

    try {
      const response = await cooperplaceApi.post(`users/${params.id}/permissions`, {
        permissionId: permission.id,
        action: action !== 'deny',
      });

      const normalized = normalizePermissions([response.data]);
      const newPermission = normalized[0] || {
        id: permission.id,
        name: permission.name,
        group: permission.group,
        slug: permission.slug,
        action,
        includedBy: getCurrentUsername(),
        includedAt: new Date().toISOString(),
      };

      user.setData({
        customPermissions: [...permissions, newPermission],
      });
      setInitialPermissions([...permissions, newPermission].map(item => ({ ...item })));
      return true
    } catch (error) {
      let message = 'Erro ao adicionar permissão'
      if (error?.response?.data?.error) {
        message = error.response.data.error
      }
      user.showSnackbar(message, { type: 'error' });
      return false
    }
  }

  async function handleActionChange(permissionId, action) {
    if (!isEditing) {
      const updated = permissions.map(permission =>
        permission.id === permissionId ? { ...permission, action } : permission
      );

      user.setData({ customPermissions: updated });
      return;
    }

    try {
      await cooperplaceApi.put(`users/${params.id}/permissions/${permissionId}`, {
        action: action !== 'deny',
      });

      const updated = permissions.map(permission =>
        permission.id === permissionId ? { ...permission, action } : permission
      );

      user.setData({ customPermissions: updated });
      setInitialPermissions(updated.map(item => ({ ...item })));
    } catch (error) {
      let message = 'Erro ao atualizar permissão'
      if (error?.response?.data?.error) {
        message = error.response.data.error
      }
      user.showSnackbar(message, { type: 'error' });
    }
  }

  async function handleRemove(permissionId) {
    const shouldRemove = window.confirm(
      'Deseja excluir esta permissão adicional?'
    );
    if (!shouldRemove) {
      return;
    }

    if (!isEditing) {
      const updated = permissions.filter(permission => permission.id !== permissionId);
      user.setData({ customPermissions: updated });
      return;
    }

    try {
      await cooperplaceApi.delete(`users/${params.id}/permissions/${permissionId}`);
      const updated = permissions.filter(permission => permission.id !== permissionId);
      user.setData({ customPermissions: updated });
      setInitialPermissions(updated.map(item => ({ ...item })));
    } catch (error) {
      let message = 'Erro ao excluir permissão'
      if (error?.response?.data?.error) {
        message = error.response.data.error
      }
      user.showSnackbar(message, { type: 'error' });
    }
  }

  const columns = useMemo(
    () => [
      {
        name: '',
        title: 'Excluir',
        className: 'align-middle',
        render: permission => (
          <div className="d-flex align-items-center justify-content-center">
            <Button
              variant="error"
              size="sm"
              className="mx-1"
              onClick={() => handleRemove(permission.id)}
            >
              <FaUnlink />
            </Button>
          </div>
        ),
      },
      {
        name: 'Permissão',
        className: 'align-middle',
        selector: permission => permission.name,
      },
      {
        name: 'Grupo',
        className: 'align-middle',
        selector: permission => permission.group,
      },
      {
        name: 'Incluído por',
        className: 'align-middle',
        selector: permission => permission.includedBy || 'Não informado',
      },
      {
        name: 'Incluído em',
        className: 'align-middle',
        selector: permission => formatDate(permission.includedAt),
      },
      {
        name: 'Ação',
        className: 'align-middle',
        render: permission => (
          <Button
            variant={permission.action === 'deny' ? 'danger' : 'success'}
            outlined
            size="xs"
            onClick={() =>
              handleActionChange(
                permission.id,
                permission.action === 'deny' ? 'allow' : 'deny'
              )
            }
          >
            {permission.action === 'deny' ? 'Negar' : 'Permitir'}
          </Button>
        ),
      },
    ],
    [permissions]
  );

  return (
    <>
      <Modal
        show={isOpen}
        onHide={handleModalClose}
        heading={
          <div className="d-flex align-items-center justify-content-between w-100">
            <Text color="dark" type="header">
              Permissões adicionais
            </Text>
          </div>
        }
        footer={null}
        body={
          <Row>
            <Col xs={12} className="d-flex justify-content-end mb-3">
              <Button onClick={addModal.open}>
                <Text type="regular" weight="500" color="white">
                  Incluir permissão
                </Text>
              </Button>
            </Col>
            <Col xs={12}>
              {(
                <DataTable
                  data={pagedPermissions}
                  columns={columns}
                  total={isEditing && !search ? totalItems : filteredPermissions.length}
                  pages={pages}
                  currentPage={currentPage}
                  defaultPerPage={perPage}
                  onSearchChanged={value => {
                    setSearch(value);
                    setCurrentPage(1);
                    if (isEditing) {
                      fetchUserPermissions({ page: 1, searchTerm: value });
                    }
                  }}
                  onPageChanged={page => setCurrentPage(page)}
                  onPerPageChanged={value => {
                    setPerPage(value);
                    setCurrentPage(1);
                  }}
                  searchPlaceholder="Nome permissão"
                  withPagination
                />
              )}

              {filteredPermissions.length === 0 ? (
                <div className="text-center py-4">
                  <Text color="dark" type="regular">
                    Nenhuma permissão adicional encontrada para este usuário.
                  </Text>
                </div>
              ) : null}
            </Col>
          </Row>
        }
      />
      <AddPermissionModal
        isOpen={addModal.isOpen}
        onClose={addModal.close}
        permissionOptions={availablePermissionOptions}
        onAdd={handleAddPermission}
      />
    </>
  );
}
